import { TagTemplateClient } from "@formant/core/client/TagTemplateClient";
import { defined } from "@formant/core/common/defined";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { ITagTemplate } from "@formant/core/model/ITagTemplate";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { ITagTemplateStore, TagTemplateFilter } from "./ITagTemplateStore";
import { StoreCache } from "./StoreCache";

export class TagTemplateStore implements ITagTemplateStore {
    private listCache = new StoreCache<
        undefined,
        DefinedEntity<ITagTemplate>[]
    >();
    private cache = new StoreCache<Uuid, DefinedEntity<ITagTemplate> | false>();

    constructor(
        private tagTemplateClient: TagTemplateClient,
        private authStore: IAuthStore,
    ) {}

    public async listAsync(
        filter?: TagTemplateFilter,
    ): Promise<DefinedEntity<ITagTemplate>[]> {
        let result = (await this.fetchList()).filter(_ => _.enabled);
        if (result && filter) {
            result = result.filter(
                _ =>
                    (filter === "telemetry" && _.isTelemetryFilter) ||
                    (filter === "event" && _.isEventFilter),
            );
        }
        return result;
    }

    public list(
        filter?: TagTemplateFilter,
    ): DefinedEntity<ITagTemplate>[] | undefined {
        let result = this.listAll()?.filter(_ => _.enabled);
        if (result && filter) {
            result = result.filter(
                _ =>
                    (filter === "telemetry" && _.isTelemetryFilter) ||
                    (filter === "event" && _.isEventFilter),
            );
        }
        return result;
    }

    public listAll(): DefinedEntity<ITagTemplate>[] | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    public get(id: Uuid): DefinedEntity<ITagTemplate> | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public async create(
        tagTemplate: ITagTemplate,
    ): Promise<DefinedEntity<ITagTemplate>> {
        const created = await this.tagTemplateClient.create(
            await this.authStore.token(),
            tagTemplate,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    public async update(
        id: Uuid,
        tagTemplate: Partial<ITagTemplate>,
    ): Promise<DefinedEntity<ITagTemplate>> {
        const updated = await this.tagTemplateClient.update(
            await this.authStore.token(),
            id,
            tagTemplate,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public save(
        tagTemplate: ITagTemplate,
    ): Promise<DefinedEntity<ITagTemplate>> {
        const { id } = tagTemplate;
        if (id) {
            return this.update(id, tagTemplate);
        } else {
            return this.create(tagTemplate);
        }
    }

    public async disable(id: Uuid): Promise<DefinedEntity<ITagTemplate>> {
        return await this.update(id, { enabled: false });
    }

    private async fetchGet(
        id: Uuid,
    ): Promise<DefinedEntity<ITagTemplate> | false> {
        const tagTemplate = await this.tagTemplateClient.get(
            await this.authStore.token(),
            id,
        );
        const value = tagTemplate || false;
        this.cache.set(id, value);
        return value;
    }

    private async fetchList(): Promise<DefinedEntity<ITagTemplate>[]> {
        const value = await this.tagTemplateClient.list(
            await this.authStore.token(),
        );
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }
}
